import { Events } from '@shared/Events';
import { FreeCrossSectionTool } from '@openlayers/tools/tool-free-crosssection';
import { FreeLongSectionTool } from '@openlayers/tools/tool-free-longsection';
import { DrawInteractionTool } from '@openlayers/tools/tool-base';
import { DistanceTool } from '@openlayers/tools/tool-distance';
import { AreaTool } from '@openlayers/tools/tool-area';
import { VolumeTool } from '@openlayers/tools/volume/tool-volumes';
import { setMapCursor } from '@openlayers/ol-utils';
import * as olExtent from 'ol/extent';
import { autorun, makeObservable, observable } from "mobx";
import { MapWrapper } from "@openlayers/MapWrapper";
import { transform as olTransform } from 'ol/proj';
import Feature from 'ol/Feature';
import { MapLocationPickerTool } from '@openlayers/tools/tool-location-picker';
import { MeasureToolInteractions } from '@openlayers/tools/tool-interactions';
import { Drawer } from '@shared/ui-native/Drawer';
import { MaterialsStore } from '@pages/trucks/store/MaterialsStore';
import { Polygon } from 'ol/geom';
import { GeofenceEditor } from '@pages/trucks/geofences/GeofenceEditor';
import { GeofencesStore } from '@pages/trucks/store/GeofencesStore';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { openVolumeWindow } from '@openlayers/tools/volume/open-dialog'
import { IFloatingWindowStoredState, WindowManager } from '@shared/ui-react/FloatingWindow';
import * as React from 'react';
import { LStorage } from '@shared/Storage';
import { CrossSectionComponent } from '@pages/cross-section/cross-section/CrossSectionComponent';
import { LongSectionComponent } from '@pages/cross-section/long-section/LongSectionComponent';
import { ICrossSectionLoadSelection } from '@pages/cross-section/cross-section/CrossSectionStore';
import { v4 as uuidv4 } from 'uuid';
import { TreeNode } from '@shared/ui-native/Tree';


export class MapToolsStore {
    private mapWrapper: MapWrapper;
    public getSelectedModels: () => TreeNode[];

    @observable.ref activeTool: any | DrawInteractionTool;
    distanceTool: DistanceTool;
    areaTool: AreaTool;
    toolInteractions: MeasureToolInteractions;

    freeCrossSectionTool: FreeCrossSectionTool;
    freeLongSectionTool: FreeLongSectionTool;
    locationPickerTool: MapLocationPickerTool;
    volumeTool: VolumeTool;
    volumeToolType: string

    @observable hasMapAlignment: boolean = false;
    @observable hasMapStation: boolean = false;
    @observable hasMeasurements: boolean = false;
    @observable openCrossSectionInNewTab = window.innerWidth < 800 ? false : LStorage.getOrDefault('openCrossSectionInNewTab', false);

    init(
        mapWrapper: MapWrapper,
        getSelectedModels: () => TreeNode[]
    ) {
        Events.on('mapStationRemoved', () => this.hasMapStation = false);
        Events.on('mapStationAdded', () => this.hasMapStation = true);
        Events.on('mapActiveAlignmentRemoved', () => this.hasMapAlignment = false);
        Events.on('mapActiveAlignmentAdded', () => this.hasMapAlignment = true);

        this.mapWrapper = mapWrapper;
        this.getSelectedModels = getSelectedModels;

        this.areaTool = new AreaTool(mapWrapper);
        this.distanceTool = new DistanceTool(mapWrapper);
        this.volumeTool = new VolumeTool(mapWrapper);
        this.volumeToolType = '';
        this.toolInteractions = new MeasureToolInteractions(mapWrapper, [this.areaTool.drawLayer.getSource(), this.distanceTool.drawLayer.getSource()]);
        this.areaTool.onDrawEndCallback = this.toolInteractions.addFeature;
        this.areaTool.onDrawRemove = this.toolInteractions.removeFeature;
        this.areaTool.onDrawProgressCallback = feature => this.toolInteractions.addGonsSnap(feature);
        this.volumeTool.onDrawEndCallback = this.toolInteractions.addFeature;
        this.volumeTool.onDrawRemove = this.toolInteractions.removeFeature;
        this.volumeTool.onDrawProgressCallback = feature => this.toolInteractions.addGonsSnap(feature);
        this.distanceTool.onDrawEndCallback = this.toolInteractions.addFeature;
        this.distanceTool.onDrawRemove = this.toolInteractions.removeFeature;
        this.distanceTool.onDrawProgressCallback = feature => this.toolInteractions.addGonsSnap(feature);

        this.freeLongSectionTool = new FreeLongSectionTool(mapWrapper, this.openLongSectionWindow);
        this.freeCrossSectionTool = new FreeCrossSectionTool(mapWrapper, (latLon1, latLon2) => this.openCrossSectionWindow(null, latLon1, latLon2, null));

        this.distanceTool.drawLayer.getSource().on('addfeature', this.onMeasurementsChange);
        this.distanceTool.drawLayer.getSource().on('removefeature', this.onMeasurementsChange);
        this.areaTool.drawLayer.getSource().on('addfeature', this.onMeasurementsChange);
        this.areaTool.drawLayer.getSource().on('removefeature', this.onMeasurementsChange);
        this.volumeTool.drawLayer.getSource().on('addfeature', this.onMeasurementsChange);
        this.volumeTool.drawLayer.getSource().on('removefeature', this.onMeasurementsChange);
    }

    constructor() {
        makeObservable(this);

        autorun(() => {
            LStorage.setItem('openCrossSectionInNewTab', this.openCrossSectionInNewTab);
        })
    }

    activateTool = (tool: MapTool) => {
        if (this.activeTool && this.activeTool.deactivate) {
            this.activeTool.deactivate();
        }
        setMapCursor(this.mapWrapper.getInstance(), '');
        if (tool) {
            setMapCursor(this.mapWrapper.getInstance(), 'crosshair');
            tool.activate();
            this.toolInteractions.activate();
        } else {
            this.toolInteractions.deactivate();
        }
        this.activeTool = tool;

        // gui
        Events.trigger('map-hide-overlays');
    }

    deactivateAllTools = () => {
        this.activateTool(null);
    };

    activateAreaTool = () => {
        this.activateTool(this.areaTool);
    };

    activateDistanceTool = () => {
        this.activateTool(this.distanceTool);
    };

    activateLongCsTool = () => {
        this.activateTool(this.freeLongSectionTool);
        this.freeLongSectionTool.startDrawing();
    };

    activateFreeCsTool = () => {
        this.activateTool(this.freeCrossSectionTool);
    };

    activateLocationPickerTool = (origin: { lat: number, lon: number }, onConfirmed, onCancel?, confirmLabel?: string) => {
        this.locationPickerTool = new MapLocationPickerTool(this.mapWrapper, origin, this.deactivateAllTools, onConfirmed, onCancel, confirmLabel);
        this.activateTool(this.locationPickerTool);
    };

    activateStockpileVolumeTool = () => {
        this.activateTool(this.volumeTool);
        this.volumeToolType = 'stockpile';
        openVolumeWindow(this);
    };

    activateModelVolumeTool = () => {
        this.activateTool(this.volumeTool);
        this.volumeToolType = 'model';
        openVolumeWindow(this);
    };

    getDrawingsExtent = () => {
        const totalExtent = olExtent.createEmpty();

        try {
            olExtent.extend(totalExtent, this.areaTool.drawLayer.getSource().getExtent());
            olExtent.extend(totalExtent, this.distanceTool.drawLayer.getSource().getExtent());
        } catch {}

        return totalExtent;
    }

    private onMeasurementsChange = () => {
        this.hasMeasurements = (this.areaTool.getDrawingsCount() + this.distanceTool.getDrawingsCount()+ this.volumeTool.getDrawingsCount()) > 0;
    }

    clearMeasurements = () => {
        this.areaTool.clear(false);
        this.distanceTool.clear(false);
        this.volumeTool.clearAll();
        this.hasMeasurements = false;
    }

    removeMeasurement = (feature: Feature) => {
        const type: FeatureType = feature?.get('type');
        if (!type) return;

        if (type === 'area-measurement') {
            this.areaTool.removeMeasurement(feature);
        } else if (type === 'volume-measurement') {
            this.volumeTool.clearMeasurement(feature);
        }
        else if (type === 'distance-measurement') {
            this.distanceTool.removeMeasurement(feature);
        }
    }

    restoreMeasurementsFromCoords = (mapMeasurements: IMapMeasurementsSavedCoords) => {
        this.clearMeasurements();
        if (mapMeasurements && (Array.isArray(mapMeasurements.distance) || Array.isArray(mapMeasurements.area))) {
            this.areaTool.clear();
            this.distanceTool.clear();
            this.volumeTool.clearAll()

            if (Array.isArray(mapMeasurements.distance)) {
                const measurements = mapMeasurements.distance.map(measurement => {
                    return measurement.map(point => olTransform(point, 'EPSG:4326', 'EPSG:3857'));
                });
                this.activateDistanceTool();
                this.distanceTool.restoreFromCoords(measurements);
                this.deactivateAllTools();
            }
            if (Array.isArray(mapMeasurements.area)) {
                const measurements = mapMeasurements.area.map(measurement => {
                    return measurement.map(outline => {
                        return outline.map(point => olTransform(point, 'EPSG:4326', 'EPSG:3857'));
                    });
                });
                this.activateAreaTool();
                this.areaTool.restoreFromCoords(measurements);
                this.deactivateAllTools();
            }
            if (Array.isArray(mapMeasurements.volume)) {
                const measurements = mapMeasurements.volume.map(measurement => {
                    return measurement.map(outline => {
                        return outline.map(point => olTransform(point, 'EPSG:4326', 'EPSG:3857'));
                    });
                });
                this.activateStockpileVolumeTool();
                this.volumeTool.restoreFromCoords(measurements);
                this.deactivateAllTools();
            }
        }
        else {
            this.deactivateAllTools();
        }
    }

    convertModelToGeofence = async (modelId: number, n: number, e: number) => {
        const el = Drawer.open(380);
        el.classList.add('overlay-loading');
        const geofencesStore = new GeofencesStore()
        const materialsStore = new MaterialsStore(geofencesStore);
        await materialsStore.fetch();
        el.classList.remove('overlay-loading');

        const features = this.mapWrapper.featureLayers.models.getFeaturesBySource({ modelId})
            .filter(f => {
                const geom = (f.getGeometry() as Polygon);
                return geom.intersectsCoordinate([e, n]);
            })

        if (features.length > 0) {
            const feature = features[0].clone();
            const layer = new VectorLayer({ source: new VectorSource({ features: [feature]}) });
            this.mapWrapper.addLayer(layer, 999, 'geofence-convert');
            GeofenceEditor.open(this.mapWrapper, geofencesStore, materialsStore, null, feature, () => {
                this.mapWrapper.removeLayer(layer);
                layer.dispose();
            });
        }
    }

    openLongSectionWindow = async (stationStart?: number, stationEnd?: number, floatingWindow?: IFloatingWindowStoredState) => {
        const activeAlignment = this.mapWrapper.featureLayers.alignments.getActiveAlignment();
        const includeModelIds = this.getSelectedModels()?.map(m => m.id);

        if (this.openCrossSectionInNewTab && !floatingWindow) {
            const url = [];
            url.push(kuura.getContextPath() + '/long_section.htm');
            url.push('?alignmentId=' + activeAlignment.modelId);
            url.push('&alignmentIndex=' + activeAlignment.index);
            if (includeModelIds) {
                url.push('&includeModelIds=' + includeModelIds.join(','));
            }
            if (stationStart) {
                url.push('&stationStart=' + stationStart);
            }
            if (stationEnd) {
                url.push('&stationEnd=' + stationEnd);
            }
            window.open(url.join(''));
        } else {
            const windowLoadSelection: ICrossSectionLoadSelection = floatingWindow?.data?.loadSelection;

            let bodyRef: LongSectionComponent = null;
            WindowManager.open({
                group: 'long-section',
                id: uuidv4(),
                title: __('js_map_measuring_tools.long_section'),
                allowMultiple: true,
                showCloseButton: true,
                preventCloseOthers: !!floatingWindow,
                disableAutoLayout: !!floatingWindow,
                resize: 'both',
                zIndex: floatingWindow?.zIndex ?? null,
                defaultSizing: floatingWindow?.sizing ?? {
                    height: window.innerHeight * 0.7,
                    width: window.innerWidth - 700
                },
                getState: () => {
                    return bodyRef?.store.getState();
                },
                body: <div className="flex-column" style={{ minWidth: 400, minHeight: 300 }}>
                    <LongSectionComponent
                        ref={el => bodyRef = el}
                        view={floatingWindow?.data?.view}
                        searchDisplacement={floatingWindow?.data?.searchDisplacement}
                        stationStart={floatingWindow?.data.stationStart ?? stationStart}
                        stationEnd={floatingWindow?.data.stationEnd ?? stationEnd}
                        selection={windowLoadSelection ?? {
                            alignmentId: activeAlignment?.modelId,
                            alignmentIndex: activeAlignment?.index,
                            includeModelIds
                        }}
                    />
                </div>
            })
        }
    }

    openCrossSectionWindow = async (station?: number, latLon1?: [number, number], latLon2?: [number, number],
                                    selectedLogpointId?: number, floatingWindow?: IFloatingWindowStoredState) => {

        const activeAlignment = this.mapWrapper.featureLayers.alignments.getActiveAlignment();
        const includeModelIds = this.getSelectedModels()?.map(m => m.id);

        if (this.openCrossSectionInNewTab && !floatingWindow) {
            if (station) {
                window.open(getCrossSectionWindowUrl(station, activeAlignment.modelId, activeAlignment.index, selectedLogpointId, includeModelIds));
            } else {
                const url = [];
                url.push(kuura.getContextPath() + '/free_cross_section.htm');
                url.push('?projectId=' + kuura.getProjectId());
                url.push('&lat1=' + latLon1[0]);
                url.push('&lon1=' + latLon1[1]);
                url.push('&lat2=' + latLon2[0]);
                url.push('&lon2=' + latLon2[1]);

                if (activeAlignment?.modelId) {
                    url.push('&alignmentId=' + activeAlignment?.modelId);
                }
                if (activeAlignment?.index) {
                    url.push('&alignmentIndex=' + activeAlignment?.index);
                }
                if (selectedLogpointId) {
                    url.push('&selectedLogpointId=' + selectedLogpointId);
                }
                if (includeModelIds.length) {
                    url.push('&includeModelIds=' + includeModelIds.join(','));
                }
                window.open(url.join(''));
            }
        } else {
            let bodyRef: CrossSectionComponent = null;
            const windowLoadSelection: ICrossSectionLoadSelection = floatingWindow?.data?.loadSelection;

            WindowManager.open({
                group: 'cross-section',
                id: uuidv4(),
                title: __(latLon1 ? 'js_map_measuring_tools.free_cross_section' : 'js_map_measuring_tools.cross_section'),
                allowMultiple: true,
                showCloseButton: true,
                preventCloseOthers: !!floatingWindow,
                disableAutoLayout: !!floatingWindow,
                resize: 'both',
                zIndex: floatingWindow?.zIndex ?? null,
                defaultSizing: floatingWindow?.sizing ?? {
                    height: window.innerHeight * 0.7,
                    width: window.innerWidth - 700
                },
                getState: () => {
                    return bodyRef?.store.getState();
                },
                body: <div className="flex-column" style={{ minWidth: 400, minHeight: 300 }}>
                    <CrossSectionComponent
                        ref={el => bodyRef = el}
                        station={floatingWindow?.data?.station ?? station}
                        lat1={floatingWindow?.data?.lat1 ?? latLon1?.[0]}
                        lon1={floatingWindow?.data?.lon1 ?? latLon1?.[1]}
                        lat2={floatingWindow?.data?.lat2 ?? latLon2?.[0]}
                        lon2={floatingWindow?.data?.lon2 ?? latLon2?.[1]}
                        view={floatingWindow?.data?.view}
                        searchDisplacement={floatingWindow?.data?.searchDisplacement}
                        deltaZFilter={floatingWindow?.data?.deltaZFilter}
                        selection={windowLoadSelection ?? {
                            alignmentId: activeAlignment?.modelId,
                            alignmentIndex: activeAlignment?.index,
                            selectedLogpointId: selectedLogpointId,
                            includeModelIds
                        }}
                    />
                </div>
            })
        }
    }
}

function getCrossSectionWindowUrl(station: number, activeAlignmentId: number, activeAlignmentIndex: number, selectedLogpointId?: number, modelIds?: number[]) {
    if (!$.isNumeric(activeAlignmentId)) {
        console.log('No selected alignment. Can\'t draw cross section.');
        return null;
    }

    const url = [];
    url.push(kuura.getContextPath());
    url.push('/cross_section.htm');
    url.push('?alignmentId=' + activeAlignmentId);
    url.push('&alignmentIndex=' + activeAlignmentIndex);
    url.push('&includeModelIds=' + activeAlignmentId);
    if (modelIds?.length) {
        url.push(',');
        url.push(modelIds.join(','));
    }

    if (station) {
        url.push('&station=' + station);
    }

    if (selectedLogpointId) {
        url.push('&selectedLogpointId=');
        url.push(selectedLogpointId);
    }

    return url.join('');
}
